import React, { useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '../../components/Layout';
import DevConsoleLayout from '../../components/DevConsoleLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import InfoTip from '../../components/InfoTip';

const statusColor = {
  sent: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  skipped: 'bg-amber-100 text-amber-700',
};

const safeString = (value) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

export default function WhatsAppLogs({
  endpoint = '/whatsapp/logs',
  title = 'WhatsApp Logs',
  subtitle = 'View every send attempt, with search, export, and event-by-event filtering.',
  useDevConsole = false,
  hideEventFilter = false,
  hideAppBanner = false,
  devKey = '',
}) {
  const { currentEvent, user } = useAuth();
  const [events, setEvents] = useState([]);
  const [eventManagers, setEventManagers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [filters, setFilters] = useState({
    eventId: 'all',
    status: '',
    trigger: '',
    search: '',
    eventManagerId: 'all',
  });

  const isGlobalView = useMemo(() => user?.role === 'event_manager', [user?.role]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await api.get('/events');
        setEvents(response.data || []);
      } catch (error) {
        console.error('Failed to fetch events:', error);
      }
    };
    fetchEvents();

    const fetchEventManagers = async () => {
      try {
        const response = await api.get('/dev/event-managers', { params: devKey ? { dev_key: devKey } : undefined });
        setEventManagers(response.data.items || []);
      } catch (error) {
        console.error('Failed to fetch event managers:', error);
      }
    };
    if (endpoint === '/dev/logs') {
      fetchEventManagers();
    }
  }, [endpoint, devKey]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filters.eventId && filters.eventId !== 'all' && !hideEventFilter) params.event_id = filters.eventId;
      if (filters.status) params.status = filters.status;
      if (filters.trigger) params.trigger = filters.trigger;
      if (filters.search) params.search = filters.search;
      if (filters.eventManagerId && filters.eventManagerId !== 'all') params.event_manager_id = filters.eventManagerId;
      if (devKey) params.dev_key = devKey;
      const response = await api.get(endpoint, { params });
      setLogs(response.data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentEvent?.id && !isGlobalView && !hideEventFilter) {
      setFilters((prev) => ({ ...prev, eventId: prev.eventId === 'all' ? currentEvent.id : prev.eventId }));
    }
  }, [currentEvent?.id, isGlobalView, hideEventFilter]);

  useEffect(() => {
    fetchLogs();
  }, [filters.eventId, filters.eventManagerId, endpoint, devKey, hideEventFilter]);

  const filteredLogs = useMemo(() => {
    const query = filters.search.trim().toLowerCase();
    if (!query) return logs;
    return logs.filter((log) => {
      const haystack = [
        log.id,
        log.event_name,
        log.event_id,
        log.guest_name,
        log.guest_id,
        log.sender_user_name,
        log.recipient_phone,
        log.recipient_phone_normalized,
        log.template_name,
        log.trigger,
        log.status,
        log.error,
        JSON.stringify(log.request_payload || {}),
        JSON.stringify(log.payload || {}),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [logs, filters.search]);

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(filteredLogs, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `whatsapp_logs_${filters.eventId || 'all'}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const exportCsv = () => {
    const columns = ['created_at', 'event_name', 'guest_name', 'status', 'trigger', 'sender_user_name', 'recipient_phone_normalized', 'template_name', 'message_id', 'error'];
    const escapeCsv = (value) => `"${safeString(value).replace(/"/g, '""')}"`;
    const rows = [
      columns.join(','),
      ...filteredLogs.map((log) => columns.map((col) => escapeCsv(log[col])).join(',')),
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `whatsapp_logs_${filters.eventId || 'all'}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const page = (
    <div className="space-y-6">
      {!hideAppBanner && (
        <div className="flex items-center gap-1">
          <h1 className="font-serif text-3xl font-bold text-wedding-charcoal">{title}</h1>
          <InfoTip content={subtitle} label={`About ${title}`} />
        </div>
      )}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-1">
            Filters
            <InfoTip content="Switch between a single event and the global log view." label="About log filters" />
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
          {!hideEventFilter ? (
            <select
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={filters.eventId}
              onChange={(e) => setFilters({ ...filters, eventId: e.target.value })}
            >
              <option value="all">All events</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name || event.id}
                </option>
              ))}
            </select>
          ) : <div />}
          <Input placeholder="Status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} />
          <Input placeholder="Trigger" value={filters.trigger} onChange={(e) => setFilters({ ...filters, trigger: e.target.value })} />
          <Input placeholder="Search logs" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
          {endpoint === '/dev/logs' ? (
            <select
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={filters.eventManagerId}
              onChange={(e) => setFilters({ ...filters, eventManagerId: e.target.value })}
            >
              <option value="all">All event managers</option>
              {eventManagers.map((manager) => (
                <option key={manager.id} value={manager.id}>
                  {manager.company_name || manager.name || manager.email}
                </option>
              ))}
            </select>
          ) : <div />}
          <div className="flex flex-wrap gap-2">
            <Button onClick={fetchLogs} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</Button>
            <Button variant="outline" onClick={exportJson} disabled={!filteredLogs.length}>Export JSON</Button>
            <Button variant="outline" onClick={exportCsv} disabled={!filteredLogs.length}>Export CSV</Button>
          </div>
        </CardContent>
      </Card>
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <div>{isGlobalView ? 'Global view enabled.' : 'Viewing logs for your current event.'}</div>
        <div>{filteredLogs.length} visible of {logs.length} loaded</div>
      </div>
      <div className="grid gap-4">
        {filteredLogs.map((log) => (
          <Card key={log.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedLog(log)}>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <CardTitle className="text-lg">{log.guest_name || 'Unknown guest'}</CardTitle>
                <Badge className={statusColor[log.status] || 'bg-slate-100 text-slate-700'}>{log.status || 'unknown'}</Badge>
              </div>
              <CardDescription>
                {log.created_at ? new Date(log.created_at).toLocaleString() : ''}{log.event_name ? ` • ${log.event_name}` : ''}
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-2 text-sm">
              <div><strong>Trigger:</strong> {log.trigger || '-'}</div>
              <div><strong>Recipient:</strong> {log.recipient_phone_normalized || log.recipient_phone || '-'}</div>
              <div><strong>Sender:</strong> {log.sender_user_name || 'System'}</div>
              <div><strong>Template:</strong> {log.template_name || '-'}</div>
              <div><strong>Message ID:</strong> {log.message_id || '-'}</div>
              <div><strong>Error:</strong> {log.error || log.provider_error?.message || '-'}</div>
            </CardContent>
          </Card>
        ))}
        {!loading && filteredLogs.length === 0 && (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">No WhatsApp attempts found for the current filter.</CardContent>
          </Card>
        )}
      </div>
      <Dialog open={Boolean(selectedLog)} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{selectedLog?.guest_name || 'Log details'}</DialogTitle>
            <DialogDescription>
              {selectedLog?.created_at ? new Date(selectedLog.created_at).toLocaleString() : ''}
            </DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <div className="grid gap-4 md:grid-cols-2 text-sm">
              <div className="space-y-2">
                <div><strong>Status:</strong> {safeString(selectedLog.status)}</div>
                <div><strong>Trigger:</strong> {safeString(selectedLog.trigger)}</div>
                <div><strong>Event:</strong> {safeString(selectedLog.event_name || selectedLog.event_id)}</div>
                <div><strong>Sender:</strong> {safeString(selectedLog.sender_user_name)}</div>
                <div><strong>Recipient:</strong> {safeString(selectedLog.recipient_phone_normalized || selectedLog.recipient_phone)}</div>
                <div><strong>Template:</strong> {safeString(selectedLog.template_name)}</div>
                <div><strong>Message ID:</strong> {safeString(selectedLog.message_id)}</div>
                <div><strong>Provider status:</strong> {safeString(selectedLog.provider_status_code)}</div>
              </div>
              <div className="space-y-3">
                <details open>
                  <summary className="cursor-pointer font-medium">Request payload</summary>
                  <pre className="mt-2 max-h-56 overflow-auto rounded bg-slate-50 p-3 text-xs">{JSON.stringify(selectedLog.request_payload || {}, null, 2)}</pre>
                </details>
                <details>
                  <summary className="cursor-pointer font-medium">Payload</summary>
                  <pre className="mt-2 max-h-56 overflow-auto rounded bg-slate-50 p-3 text-xs">{JSON.stringify(selectedLog.payload || {}, null, 2)}</pre>
                </details>
                <details>
                  <summary className="cursor-pointer font-medium">Provider response</summary>
                  <pre className="mt-2 max-h-56 overflow-auto rounded bg-slate-50 p-3 text-xs">{JSON.stringify(selectedLog.provider_response || selectedLog.provider_error || {}, null, 2)}</pre>
                </details>
                <details>
                  <summary className="cursor-pointer font-medium">Metadata</summary>
                  <pre className="mt-2 max-h-56 overflow-auto rounded bg-slate-50 p-3 text-xs">{JSON.stringify(selectedLog.metadata || {}, null, 2)}</pre>
                </details>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );

  if (useDevConsole || endpoint === '/dev/logs') {
    return <DevConsoleLayout title={title} subtitle={subtitle}>{page}</DevConsoleLayout>;
  }

  return <DashboardLayout>{page}</DashboardLayout>;
}
